import { useEffect } from "react";

/**
 * Per-route document metadata for the SPA: title, description, robots, canonical and the matching Open Graph / Twitter
 * tags, so a shared or bookmarked page reads right everywhere. The crawler-facing defaults live in public/index.html;
 * this keeps them in step once the app is running.
 *
 *   useSeo({ title: "Mine annonser", description: "...", noindex: true })
 *
 * `title` is suffixed with the brand unless `raw` is set. Portal/lab pages should pass `noindex`.
 */
const BRAND = "MagicAd by DigiHome";
const DEFAULT_DESCRIPTION =
  "Last opp mobilbildene eller lim inn FINN-/Airbnb-annonsen. MagicAd optimaliserer, styler og møblerer bildene som en profesjonell boligstylist – ærlig, merket og klart på minutter.";

const setMeta = (selector, attrs) => {
  let el = document.head.querySelector(selector);
  if (!el) {
    el = document.createElement(selector.startsWith("link") ? "link" : "meta");
    Object.entries(attrs).forEach(([k, v]) => k !== "content" && k !== "href" && el.setAttribute(k, v));
    document.head.appendChild(el);
  }
  if (attrs.content !== undefined) el.setAttribute("content", attrs.content);
  if (attrs.href !== undefined) el.setAttribute("href", attrs.href);
};

export const useSeo = ({ title, description = DEFAULT_DESCRIPTION, noindex = false, raw = false, image } = {}) => {
  useEffect(() => {
    const full = title ? (raw ? title : `${title} – ${BRAND}`) : `${BRAND} – Bilder som leier ut`;
    const url = `${window.location.origin}${window.location.pathname}`;
    document.title = full;
    setMeta('meta[name="description"]', { name: "description", content: description });
    setMeta('meta[name="robots"]', { name: "robots", content: noindex ? "noindex, nofollow" : "index, follow, max-image-preview:large" });
    setMeta('link[rel="canonical"]', { rel: "canonical", href: noindex ? `${window.location.origin}/` : url });
    setMeta('meta[property="og:title"]', { property: "og:title", content: full });
    setMeta('meta[property="og:description"]', { property: "og:description", content: description });
    setMeta('meta[property="og:url"]', { property: "og:url", content: noindex ? `${window.location.origin}/` : url });
    setMeta('meta[name="twitter:title"]', { name: "twitter:title", content: full });
    setMeta('meta[name="twitter:description"]', { name: "twitter:description", content: description });
    if (image) {
      setMeta('meta[property="og:image"]', { property: "og:image", content: image });
      setMeta('meta[name="twitter:image"]', { name: "twitter:image", content: image });
    }
  }, [title, description, noindex, raw, image]);
};
